import type { CSSProperties, JSX } from 'react';
import type { StyledComponent } from '@emotion/styled';
import type { LowLevelStylefunctionArguments, ResponsiveValue, styleFn } from 'styled-system';
import { style } from 'styled-system';

/* eslint-disable max-lines */

import type { ThemeProps } from './theme';

export type AsProps = {
  /**
   * To use styles from a styled component but change the element that’s rendered, you can use the as prop.
   *
   * [docs](https://emotion.sh/docs/styled#as-prop)
   */
  as?: React.ElementType;
};

export type DataTestIdProps = {
  'data-testid'?: string;
};

export type StyledTagOverwrite<
  Tag extends keyof JSX.IntrinsicElements,
  OverwriteProps extends object,
> = StyledComponent<
  AsProps & OverwriteProps,
  Omit<JSX.IntrinsicElements[Tag], keyof OverwriteProps>,
  { theme?: ThemeProps }
>;

type SystemProps<T extends keyof CSSProperties> = {
  [K in T]?: ResponsiveValue<CSSProperties[T]>;
};

const transformValueFromTheme = (n, theme) => {
  let val = theme[n];
  if (val !== 0) {
    val = val || n;
  }

  return typeof val !== 'string' ? `${val}px` : val;
};

// Default width/height/top etc. helpers from styled-system are not
// using theme values, this is why we are creating our own.
//
// More info:
//   https://styled-system.com/#width
//   https://styled-system.com/api#customize

export interface ThemeDimensionsProps {
  width?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
}

export interface ThemeWidthProps {
  themeWidth?: ResponsiveValue<string | number>;
}

export type TextDecorationProps = SystemProps<'textDecoration'>;

export type TextUnderlinePositionProps = SystemProps<'textUnderlinePosition'>;

export interface BoxSizingProps {
  boxSizing?: ResponsiveValue<CSSProperties['boxSizing']>;
}

const createStyleFn = <N, S>(variant: LowLevelStylefunctionArguments<N, S>) =>
  style(variant) as unknown as styleFn;

export const themeWidth = style({
  prop: 'themeWidth',
  cssProperty: 'width',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeMaxWidthProps {
  themeMaxWidth?: ResponsiveValue<string>;
}

export const themeMaxWidth = style({
  prop: 'themeMaxWidth',
  cssProperty: 'maxWidth',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeMinWidthProps {
  themeMinWidth?: ResponsiveValue<string>;
}

export const themeMinWidth = style({
  prop: 'themeMinWidth',
  cssProperty: 'minWidth',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeHeightProps {
  themeHeight?: ResponsiveValue<string | number>;
}

export const themeHeight = style({
  prop: 'themeHeight',
  cssProperty: 'height',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeMaxHeightProps {
  themeMaxHeight?: ResponsiveValue<string>;
}

export const themeMaxHeight = style({
  prop: 'themeMaxHeight',
  cssProperty: 'maxHeight',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeMinHeightProps {
  themeMinHeight?: ResponsiveValue<string>;
}

export const themeMinHeight = style({
  prop: 'themeMinHeight',
  cssProperty: 'minHeight',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeTopProps {
  themeTop?: ResponsiveValue<string>;
}

export const themeTop = style({
  prop: 'themeTop',
  cssProperty: 'top',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeRightProps {
  themeRight?: ResponsiveValue<string>;
}

export const themeRight = style({
  prop: 'themeRight',
  cssProperty: 'right',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeBottomProps {
  themeBottom?: ResponsiveValue<string>;
}

export const themeBottom = style({
  prop: 'themeBottom',
  cssProperty: 'bottom',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export interface ThemeLeftProps {
  themeLeft?: ResponsiveValue<string>;
}

export const themeLeft = style({
  prop: 'themeLeft',
  cssProperty: 'left',
  key: 'space',
  transformValue: transformValueFromTheme,
});

export type ColumnsProps = SystemProps<'columns'>;

export const columns = style({
  prop: 'columns',
  cssProperty: 'columns',
});

export type CursorProps = SystemProps<'cursor'>;

export const cursor = style({
  prop: 'cursor',
  cssProperty: 'cursor',
});

export const boxSizing = style({
  prop: 'boxSizing',
});

export interface FlexGrowProps {
  /**
   * The flex-grow CSS property sets the flex grow factor of a flex item's main size.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-grow)
   */
  flexGrow?: ResponsiveValue<string | number>;
}

export const flexGrow = style({
  prop: 'flexGrow',
});

export type FlexShrinkProps = SystemProps<'flexShrink'>;

export const flexShrink = style({
  prop: 'flexShrink',
});

export interface FlexGapProps {
  gap?: ResponsiveValue<string | number>;
}

export const flexGap = createStyleFn({
  prop: 'gap',
  key: 'space',
});

export const flexRowGap = createStyleFn({
  prop: 'rowGap',
  key: 'space',
});

export const flexColumnGap = createStyleFn({
  prop: 'columnGap',
  key: 'space',
});

export const textDecoration = style({
  prop: 'textDecoration',
});

export const textUnderlinePosition = style({
  prop: 'textUnderlinePosition',
});

export type TextDecorationThicknessProps = SystemProps<'textDecorationThickness'>;

export const textDecorationThickness = style({
  prop: 'textDecorationThickness',
});

export type WhiteSpaceProps = SystemProps<'whiteSpace'>;

export const whiteSpace = createStyleFn({
  prop: 'whiteSpace',
});

export type TextOverflowProps = SystemProps<'textOverflow'>;

export const textOverflow = createStyleFn({
  prop: 'textOverflow',
});

export interface TextIndentProps {
  textIndent?: CSSProperties['textIndent'];
}

export const textIndent = createStyleFn({
  prop: 'textIndent',
});

export const overflowY = createStyleFn({
  prop: 'overflowY',
});

export type OverflowYProps = SystemProps<'overflowY'>;

export type OverflowXProps = SystemProps<'overflowX'>;

export const overflowX = createStyleFn({
  prop: 'overflowX',
});

export type BorderTopStyleProps = SystemProps<'borderTopStyle'>;

export const borderTopStyle = style({
  prop: 'borderTopStyle',
});

export type BorderRightStyleProps = SystemProps<'borderRightStyle'>;

export const borderRightStyle = style({
  prop: 'borderRightStyle',
});

export type BorderBottomStyleProps = SystemProps<'borderBottomStyle'>;

export const borderBottomStyle = style({
  prop: 'borderBottomStyle',
});

export type BorderTopColorProps = SystemProps<'borderTopColor'>;

export const borderTopColor = style({
  prop: 'borderTopColor',
  key: 'colors',
});

export type BorderBottomColorProps = SystemProps<'borderBottomColor'>;

export const borderBottomColor = style({
  prop: 'borderBottomColor',
  key: 'colors',
});

export type BorderRightColorProps = SystemProps<'borderRightColor'>;

export const borderRightColor = style({
  prop: 'borderRightColor',
  key: 'colors',
});

export const borderRightWidth = style({
  prop: 'borderRightWidth',
  key: 'borderWidths',
});

export const borderLeftWidth = style({
  prop: 'borderLeftWidth',
  key: 'borderWidths',
});

export type BorderTopWidthProps = SystemProps<'borderTopWidth'>;

export type BorderLeftWidthProps = SystemProps<'borderLeftWidth'>;

export type BorderRightWidthProps = SystemProps<'borderRightWidth'>;

export const borderTopWidth = style({
  prop: 'borderTopWidth',
  key: 'borderWidths',
});

export type BorderBottomWidthProps = SystemProps<'borderBottomWidth'>;

export const borderBottomWidth = style({
  prop: 'borderBottomWidth',
  key: 'borderWidths',
});

export type PointerEventProps = SystemProps<'pointerEvents'>;

export const pointerEvents = style({
  prop: 'pointerEvents',
});

export const fill = createStyleFn({
  prop: 'fill',
  key: 'colors',
});

export type FilterProps = SystemProps<'filter'>;

export const filter = style({
  prop: 'filter',
});

export const stroke = style({
  prop: 'stroke',
  key: 'colors',
});

export type StrokeWidthProps = SystemProps<'strokeWidth'>;

export const strokeWidth = style({
  prop: 'strokeWidth',
  key: 'space',
});

export interface BorderRadiusTR {
  borderRadiusTR?: CSSProperties['borderTopRightRadius'];
}

export const borderRadiusTR = style({
  prop: 'borderRadiusTR',
  cssProperty: 'borderTopRightRadius',
  key: 'radii',
  transformValue: transformValueFromTheme,
});

export interface BorderRadiusTL {
  borderRadiusTL?: CSSProperties['borderTopLeftRadius'];
}

export const borderRadiusTL = style({
  prop: 'borderRadiusTL',
  cssProperty: 'borderTopLeftRadius',
  key: 'radii',
  transformValue: transformValueFromTheme,
});

export type BorderRadiusBR = SystemProps<'borderBottomRightRadius'>;

export const borderRadiusBR = style({
  prop: 'borderRadiusBR',
  cssProperty: 'borderBottomRightRadius',
  key: 'radii',
  transformValue: transformValueFromTheme,
});

export type BorderRadiusBL = SystemProps<'borderBottomLeftRadius'>;

export const borderRadiusBL = style({
  prop: 'borderRadiusBL',
  cssProperty: 'borderBottomLeftRadius',
  key: 'radii',
  transformValue: transformValueFromTheme,
});

export const translate3d = style({
  prop: 'translate3d',
  cssProperty: 'transform',
  transformValue: val => `translate3d(${val})`,
});

export interface Translate3DProps {
  translate3d?: ResponsiveValue<[string | number, string | number, string | number]>;
}

export interface TranslateXProps {
  translateX?: ResponsiveValue<string | number>;
}

export const translateX = style({
  prop: 'translateX',
  cssProperty: 'transform',
  transformValue: val => `translateX(${val})`,
});

export const scale = style({
  prop: 'scale',
  cssProperty: 'transform',
  transformValue: val => `scale(${val})`,
});

export const rotate = style({
  prop: 'rotate',
  cssProperty: 'transform',
  transformValue: val => `rotate(${val}deg)`,
});

export interface RotateProps {
  rotate?: ResponsiveValue<string | number>;
}

export const maskImage = style({
  prop: 'maskImage',
  cssProperty: 'maskImage',
});

export type MaskImageProps = SystemProps<'maskImage'>;

export type TransitionProps = SystemProps<'transition'>;

export const transition = style({
  prop: 'transition',
  cssProperty: 'transition',
});

export type TransitionPropertyProps = SystemProps<'transitionProperty'>;

export const transitionProperty = style({
  prop: 'transitionProperty',
  cssProperty: 'transitionProperty',
});

export type TransitionTimingFunctionProps = SystemProps<'transitionTimingFunction'>;

export const transitionTimingFunction = style({
  prop: 'transitionTimingFunction',
  cssProperty: 'transitionTimingFunction',
});

export type TransitionDurationProps = SystemProps<'transitionDuration'>;

export const transitionDuration = style({
  prop: 'transitionDuration',
  cssProperty: 'transitionDuration',
});

export type TransformProps = SystemProps<'transform'>;

export const transform = style({
  prop: 'transform',
});

export type TextTransformProps = SystemProps<'textTransform'>;

export const textTransform = style({
  prop: 'textTransform',
  cssProperty: 'textTransform',
});

export const lineClamp = style({
  prop: 'lineClamp',
  cssProperty: 'WebkitLineClamp',
});

export type LineClampProps = SystemProps<'lineClamp'>;

export const boxOrient = style({
  prop: 'boxOrient',
  cssProperty: 'WebkitBoxOrient',
});

export type BoxOrientProps = SystemProps<'boxOrient'>;

export const listStyle = style({
  prop: 'listStyle',
});

export type ListStyleProps = SystemProps<'listStyle'>;

export const wordBreak = createStyleFn({
  prop: 'wordBreak',
});

export type WordBreakProps = SystemProps<'wordBreak'>;

export type ObjectFitProps = SystemProps<'objectFit'>;

export const objectFit = style({
  prop: 'objectFit',
});

export const outline = style({
  prop: 'outline',
});

export type OutlineColorProps = SystemProps<'outlineColor'>;

export const outlineColor = style({
  prop: 'outlineColor',
  key: 'colors',
});

export const themeBoxShadow = style({
  prop: 'boxShadow',
  key: 'boxShadows',
});

export const themeOpacity = style({
  prop: 'themeOpacity',
  cssProperty: 'opacity',
  key: 'opacity',
});

export const textShadow = style({
  prop: 'textShadow',
});

export type AspectRatioProps = SystemProps<'aspectRatio'>;

export const aspectRatio = style({
  prop: 'aspectRatio',
});

export interface VisibilityProps {
  visibility?: ResponsiveValue<CSSProperties['visibility']>;
}

export const visibility = style({
  prop: 'visibility',
});

export type FontVariantNumericProps = SystemProps<'fontVariantNumeric'>;

export const fontVariantNumeric = style({
  prop: 'fontVariantNumeric',
});

export type ScrollMarginTopProps = SystemProps<'scrollMarginTop'>;

export const scrollMarginTop = createStyleFn({
  prop: 'scrollMarginTop',
  key: 'space',
});
